import { lazy, Suspense } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Navbar } from '@/components/sections/navbar';
import { Hero } from '@/components/sections/hero';
import { StatsStrip } from '@/components/sections/stats-strip';
import { AboutSection } from '@/components/sections/about-section';
import { PartnersSection } from '@/components/sections/partners-section';
import { Declencheurs } from '@/components/sections/declencheurs';
import { ThreePeurs } from '@/components/sections/three-peurs';
import { MethodeFiveQ } from '@/components/sections/methode-five-q';
import { CostBreakdown } from '@/components/sections/cost-breakdown';
import { MidPageCta } from '@/components/sections/mid-page-cta';
import { GuideLeadMagnet } from '@/components/sections/guide-lead-magnet';
import { Testimonials } from '@/components/sections/testimonials';
import { CommunautePro } from '@/components/sections/communaute-pro';
import { OfferBouclier } from '@/components/sections/offer-bouclier';
import { FaqSection } from '@/components/sections/faq-section';
import { SiteFooter } from '@/components/sections/site-footer';
import { SectionDivider } from '@/components/ui/section-divider';
import { SectionRail } from '@/components/ui/section-rail';

// Phase 27.39 — lazy-load 5 below-fold sections. Root cause du crash React
// error #306 en Phase 27.34 = Rolldown qui inline UN composant lazy (calculatrice-amg)
// dans index-*.js au lieu d'un chunk dédié → `import('...')` résolvait vers le
// main bundle où `m.CalculatriceAMG` était undefined. Fix dans vite.config.ts
// `manualChunks` qui force chunks dédiés. Les chemins relatifs ci-dessous sont
// défensifs (anti-ambiguïté future avec alias `@/`), pas le fix principal.
const ComparatorCollectifVsAMG = lazy(() =>
  import('../components/sections/comparator-collectif-amg').then((m) => ({
    default: m.ComparatorCollectifVsAMG
  }))
);
const DiagnosticQuiz5Q = lazy(() =>
  import('../components/sections/diagnostic-quiz-5q').then((m) => ({
    default: m.DiagnosticQuiz5Q
  }))
);
const CalculatriceAMG = lazy(() =>
  import('../components/sections/calculatrice-amg').then((m) => ({
    default: m.CalculatriceAMG
  }))
);
const PremiumEstimator = lazy(() =>
  import('../components/sections/premium-estimator').then((m) => ({
    default: m.PremiumEstimator
  }))
);
const ContactForm = lazy(() =>
  import('../components/sections/contact-form').then((m) => ({
    default: m.ContactForm
  }))
);

function SectionFallback({ height = 1200 }: { height?: number }) {
  return (
    <div
      aria-hidden="true"
      style={{
        minHeight: `${height}px`,
        contentVisibility: 'auto',
        containIntrinsicSize: `auto ${height}px`
      }}
    />
  );
}

/**
 * Homepage flow — Phase 22 reorder (marketing-psychology commitment ladder) :
 * Pain framing → Solution proof → Commitment ladder (quiz/calc/estimator) →
 * Social proof → Big ask (Offer/Form). Reduces friction to mid-page CTA.
 */
function LandingPage() {
  return (
    <div className="min-h-[100dvh] bg-background text-foreground">
      <Navbar />
      <SectionRail />
      <main id="main">
        {/* Pain + framing */}
        <Hero />
        <div className="mx-auto h-px w-24 bg-secondary/40" aria-hidden="true" />
        <StatsStrip />
        <SectionDivider variant="wave" from="background" to="cream-deep" />
        <AboutSection />
        <SectionDivider variant="wave" from="cream-deep" to="sage-soft" />
        <Declencheurs />
        <ThreePeurs />
        <SectionDivider variant="wave" from="sage-soft" to="cream-deep" />

        {/* Solution proof */}
        <MethodeFiveQ />
        <SectionDivider variant="curve" from="cream-deep" to="peach-soft" />
        <CostBreakdown />
        <SectionDivider variant="wave" from="peach-soft" to="cream" />
        <Suspense fallback={<SectionFallback height={1100} />}>
          <ComparatorCollectifVsAMG />
        </Suspense>
        <div className="mx-auto h-px w-24 bg-secondary/40" aria-hidden="true" />

        {/* Trust signal institutionnel — Distributrice agréée iA (Phase 27.49) */}
        <PartnersSection />
        <div className="mx-auto h-px w-24 bg-secondary/40" aria-hidden="true" />

        {/* Mid-page CTA — catches warm visitor mid-funnel */}
        <MidPageCta />

        {/* Lead magnet — guide gratuit (rend /guide découvrable depuis l'accueil) */}
        <GuideLeadMagnet />

        {/* Commitment ladder — small yes → bigger yes. Phase 27.41 : un Suspense
            par composant pour fault isolation (un chunk qui fail → seul ce
            composant tombe en fallback, les 2 autres restent). */}
        <Suspense fallback={<SectionFallback height={1000} />}>
          <DiagnosticQuiz5Q />
        </Suspense>
        <Suspense fallback={<SectionFallback height={1100} />}>
          <CalculatriceAMG />
        </Suspense>
        <Suspense fallback={<SectionFallback height={900} />}>
          <PremiumEstimator />
        </Suspense>
        <SectionDivider variant="wave" from="cream-deep" to="sage-soft" />

        {/* Social proof → Big ask */}
        <Testimonials />
        <SectionDivider variant="wave" from="sage-soft" to="cream-deep" />
        <CommunautePro />
        <SectionDivider variant="curve" from="cream-deep" to="terracotta" />
        <OfferBouclier />
        <SectionDivider variant="wave" from="terracotta" to="background" flip />
        <FaqSection />
        <Suspense fallback={<SectionFallback height={1000} />}>
          <ContactForm />
        </Suspense>
      </main>
      <SiteFooter />
    </div>
  );
}

export const Route = createFileRoute('/')({
  component: LandingPage
});
